import React, { useState, useCallback } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  Grid,
  Card,
  CardContent,
} from "@mui/material";
import { Add as AddIcon } from "@mui/icons-material";
import axios from "axios";
import config from "./global/config";

const AchatInstallmentPaymentModal = ({
  open,
  handleClose,
  purchaseData,
  onPaymentComplete,
}) => {
  const [formData, setFormData] = useState({
    numberOfInstallments: "",
    firstPaymentAmount: "",
    firstPaymentDate: "",
    mode_paiement: "",
    reference_paiement: "",
    commentaire: "",
  });
  const [installments, setInstallments] = useState([]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [editingInstallment, setEditingInstallment] = useState(null);
  const [isAddingNew, setIsAddingNew] = useState(false);
  const [newInstallment, setNewInstallment] = useState({
    date_echeance: "",
    montant: "",
  });
  const [hasWarning, setHasWarning] = useState(false);

  const calculateTotalAmount = useCallback(() => {
    return installments.reduce(
      (sum, inst) => sum + parseFloat(inst.montant),
      0
    );
  }, [installments]);

  const calculateRemainingAmount = useCallback(() => {
    const totalInstallments = calculateTotalAmount();
    const purchaseTotal = parseFloat(purchaseData?.montant_total || 0);
    return purchaseTotal - totalInstallments;
  }, [calculateTotalAmount, purchaseData?.montant_total]);

  const validateTotalAmount = useCallback(() => {
    const totalInstallments = calculateTotalAmount();
    const purchaseTotal = parseFloat(purchaseData?.montant_total || 0);
    return Math.abs(totalInstallments - purchaseTotal) < 0.01;
  }, [calculateTotalAmount, purchaseData?.montant_total]);

  const handleAddNewInstallment = () => {
    const remainingAmount = calculateRemainingAmount();

    if (remainingAmount <= 0) {
      setError("Cannot add more installments - total amount has been reached");
      return;
    }

    setIsAddingNew(true);
    const lastInstallment = installments[installments.length - 1];
    const nextNumber = lastInstallment ? lastInstallment.numero_tranche + 1 : 1;

    // Set default date to one month after the last installment
    let defaultDate = new Date();
    if (lastInstallment) {
      defaultDate = new Date(lastInstallment.date_echeance);
      defaultDate.setMonth(defaultDate.getMonth() + 1);
    }

    // Automatically set the amount to the remaining balance
    setNewInstallment({
      date_echeance: defaultDate.toISOString().split("T")[0],
      montant: remainingAmount.toFixed(2),
    });
  };

  const handleSaveNewInstallment = () => {
    if (!newInstallment.date_echeance || !newInstallment.montant) {
      setError("Please fill in all fields for the new installment");
      return;
    }

    const newAmount = parseFloat(newInstallment.montant);
    const remainingAmount = calculateRemainingAmount();

    if (newAmount > remainingAmount) {
      setError(
        `The installment amount cannot exceed the remaining balance of ${remainingAmount.toFixed(2)}`
      );
      return;
    }

    const newInstallmentObj = {
      numero_tranche: installments.length + 1,
      date_echeance: newInstallment.date_echeance,
      montant: newAmount,
    };

    setInstallments([...installments, newInstallmentObj]);
    setIsAddingNew(false);
    setNewInstallment({ date_echeance: "", montant: "" });
    setSuccessMessage("New installment added successfully");

    // Check if we've reached the total amount
    const newTotal = calculateTotalAmount() + newAmount;
    const purchaseTotal = parseFloat(purchaseData?.montant_total || 0);

    if (Math.abs(newTotal - purchaseTotal) < 0.01) {
      setHasWarning(false);
      setError("");
    }
  };

  const handleInstallmentDoubleClick = (installment) => {
    if (installment.numero_tranche === 1) {
      setError("Cannot edit first installment");
      return;
    }
    setEditingInstallment({
      ...installment,
      date_echeance_edit: installment.date_echeance,
      montant_edit: installment.montant,
    });
  };

  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setEditingInstallment((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSaveEdit = () => {
    const newInstallments = installments.map((inst) =>
      inst.numero_tranche === editingInstallment.numero_tranche
        ? {
            ...inst,
            date_echeance: editingInstallment.date_echeance_edit,
            montant: parseFloat(editingInstallment.montant_edit),
          }
        : inst
    );

    const totalAmount = newInstallments.reduce(
      (sum, inst) => sum + parseFloat(inst.montant),
      0
    );

    if (Math.abs(totalAmount - parseFloat(purchaseData.montant_total)) > 0.01) {
      setHasWarning(true);
      setError(
        `Warning: Total amount (${totalAmount.toFixed(2)}) does not match purchase amount (${purchaseData.montant_total}). You can still proceed if this is intentional.`
      );
    } else {
      setHasWarning(false);
      setError("");
    }

    setInstallments(newInstallments);
    setEditingInstallment(null);
    setSuccessMessage("Installment updated successfully");
  };

  const calculateInstallments = useCallback(() => {
    if (!formData.numberOfInstallments || !formData.firstPaymentAmount) return;

    const totalAmount = parseFloat(purchaseData?.montant_total || 0);
    const firstPayment = parseFloat(formData.firstPaymentAmount);
    const numberOfInstallments = parseInt(formData.numberOfInstallments);
    const remainingAmount = totalAmount - firstPayment;
    const installmentAmount = remainingAmount / (numberOfInstallments - 1);

    let newInstallments = [];
    let currentDate = new Date(formData.firstPaymentDate);

    // First payment
    newInstallments.push({
      numero_tranche: 1,
      date_echeance: currentDate.toISOString().split("T")[0],
      montant: firstPayment,
    });

    // Calculate subsequent installments
    for (let i = 2; i <= numberOfInstallments; i++) {
      currentDate.setMonth(currentDate.getMonth() + 1);
      newInstallments.push({
        numero_tranche: i,
        date_echeance: currentDate.toISOString().split("T")[0],
        montant: installmentAmount,
      });
    }

    setInstallments(newInstallments);
  }, [formData, purchaseData]);

  const validateForm = useCallback(() => {
    if (!formData.numberOfInstallments || formData.numberOfInstallments < 2) {
      setError("Number of installments must be at least 2");
      return false;
    }

    if (!formData.firstPaymentAmount || formData.firstPaymentAmount <= 0) {
      setError("Please enter a valid first payment amount");
      return false;
    }

    if (!formData.firstPaymentDate) {
      setError("Please select the first payment date");
      return false;
    }

    if (!formData.mode_paiement) {
      setError("Please select a payment method");
      return false;
    }

    const totalAmount = parseFloat(purchaseData?.montant_total || 0);
    if (parseFloat(formData.firstPaymentAmount) >= totalAmount) {
      setError("First payment cannot be greater than or equal to total amount");
      return false;
    }

    return true;
  }, [formData, purchaseData]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    setError("");
    setSuccessMessage("");
  };

  const createApiInstance = () => {
    const instance = axios.create({
      baseURL: config.apiUrl,
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
        "Content-Type": "application/x-www-form-urlencoded",
      },
      withCredentials: true,
      timeout: 15000,
    });

    instance.interceptors.request.use(
      (config) => {
        if (config.method.toLowerCase() === "options") {
          config.withCredentials = false;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    instance.interceptors.response.use(
      (response) => {
        console.log("API Response:", {
          status: response.status,
          data: response.data,
        });
        return response;
      },
      (error) => {
        console.error("Response Error:", {
          message: error.message,
          response: error.response?.data,
          status: error.response?.status,
          config: error.config,
        });

        let errorMessage = "An error occurred. ";
        if (error.response) {
          errorMessage += `Server responded with: ${error.response.data?.message || error.response.statusText}`;
        } else if (error.request) {
          errorMessage +=
            "No response received from server. Please check your network connection.";
        } else {
          errorMessage += error.message;
        }

        return Promise.reject(new Error(errorMessage));
      }
    );

    return instance;
  };

  const handleSubmit = async () => {
    const api = createApiInstance();
    setLoading(true);
    setError("");
    setSuccessMessage("");

    if (!validateTotalAmount()) {
      const proceed = window.confirm(
        "The total installment amount does not match the purchase amount. Are you sure you want to proceed?"
      );
      if (!proceed) {
        setLoading(false);
        return;
      }
    }

    try {
      if (!validateForm()) {
        setLoading(false);
        return;
      }

      // Create main installment plan
      const mainInstallmentData = new URLSearchParams();
      mainInstallmentData.append("METHOD", "POST");
      mainInstallmentData.append("id_achat", purchaseData.id_achat);
      mainInstallmentData.append(
        "nombre_tranches",
        formData.numberOfInstallments
      );
      mainInstallmentData.append("montant_total", purchaseData.montant_total);
      mainInstallmentData.append(
        "montant_par_tranche",
        (
          parseFloat(purchaseData.montant_total) /
          parseInt(formData.numberOfInstallments)
        ).toFixed(2)
      );
      mainInstallmentData.append("date_debut", formData.firstPaymentDate);
      mainInstallmentData.append("categorie_code", "1");
      mainInstallmentData.append("periodicite_jours", "30");
      mainInstallmentData.append("statut_tranches", "En cours");
      mainInstallmentData.append("user_cre", "USER");

      const mainInstallmentResponse = await api.post(
        "paiements_fournisseurs_tranches.php",
        mainInstallmentData
      );
      const installmentPlanId = mainInstallmentResponse.data.id;

      // Create individual installments
      for (const installment of installments) {
        const installmentData = new URLSearchParams();
        installmentData.append("METHOD", "POST");
        installmentData.append("id_paiement_tranche", installmentPlanId);
        installmentData.append("numero_tranche", installment.numero_tranche);
        installmentData.append("date_echeance", installment.date_echeance);
        installmentData.append("montant", installment.montant.toString());
        installmentData.append("mode_paiement", formData.mode_paiement);
        installmentData.append(
          "reference_paiement",
          formData.reference_paiement || ""
        );
        installmentData.append("commentaire", formData.commentaire || "");
        installmentData.append(
          "statut_reglements",
          installment.numero_tranche === 1 ? "Payé" : "En attente"
        );

        if (installment.numero_tranche === 1) {
          installmentData.append("date_paiement", installment.date_echeance);
        }

        installmentData.append("user_cre", "USER");

        await api.post("reglements_tranches_fournisseurs.php", installmentData);
      }

      // Update purchase status
      const purchaseUpdateData = new URLSearchParams();
      purchaseUpdateData.append("METHOD", "PUT");
      purchaseUpdateData.append("statut_paiement", "Partiel");
      purchaseUpdateData.append("type_paiement", "Tranche");
      purchaseUpdateData.append("user_upd", "USER");
      purchaseUpdateData.append("numero_achat", purchaseData.numero_achat);
      purchaseUpdateData.append("date_achat", purchaseData.date_achat);
      purchaseUpdateData.append("id_fournisseur", purchaseData.id_fournisseur);
      purchaseUpdateData.append("montant_total", purchaseData.montant_total);
      purchaseUpdateData.append("categorie_code", purchaseData.categorie_code);

      await api.post(
        `achats.php?id_achat=${purchaseData.id_achat}`,
        purchaseUpdateData
      );

      setSuccessMessage("Installment plan created successfully");
      setTimeout(() => {
        onPaymentComplete();
        handleClose();
        setFormData({
          numberOfInstallments: "",
          firstPaymentAmount: "",
          firstPaymentDate: "",
          mode_paiement: "",
          reference_paiement: "",
          commentaire: "",
        });
        setInstallments([]);
      }, 1500);
    } catch (error) {
      console.error("Payment error:", error);
      setError(
        `Error: ${error.message || "An unexpected error occurred"}. Please try again.`
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Box>
          <Typography variant="h5" component="div" sx={{ mb: 1 }}>
            Setup Installment Payment Plan
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Purchase Reference: {purchaseData?.numero_achat} | Total Amount: €
            {purchaseData?.montant_total || 0}
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Grid container spacing={3}>
          {/* Left Side - Payment Form */}
          <Grid item xs={12} md={6}>
            <Card variant="outlined" sx={{ height: "100%" }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Payment Details
                </Typography>

                {error && (
                  <Alert
                    severity={hasWarning ? "warning" : "error"}
                    sx={{ my: 2 }}
                  >
                    {error}
                  </Alert>
                )}

                {successMessage && (
                  <Alert severity="success" sx={{ my: 2 }}>
                    {successMessage}
                  </Alert>
                )}

                <TextField
                  fullWidth
                  label="Number of Installments"
                  name="numberOfInstallments"
                  type="number"
                  value={formData.numberOfInstallments}
                  onChange={handleChange}
                  margin="normal"
                  required
                  inputProps={{ min: 2 }}
                />

                <TextField
                  fullWidth
                  label="First Payment Amount"
                  name="firstPaymentAmount"
                  type="number"
                  value={formData.firstPaymentAmount}
                  onChange={handleChange}
                  margin="normal"
                  required
                  inputProps={{
                    min: 0,
                    max: purchaseData?.montant_total,
                    step: "0.01",
                  }}
                />

                <TextField
                  fullWidth
                  label="First Payment Date"
                  name="firstPaymentDate"
                  type="date"
                  value={formData.firstPaymentDate}
                  onChange={handleChange}
                  margin="normal"
                  required
                  InputLabelProps={{ shrink: true }}
                />

                <FormControl fullWidth margin="normal" required>
                  <InputLabel>Payment Method</InputLabel>
                  <Select
                    name="mode_paiement"
                    value={formData.mode_paiement}
                    onChange={handleChange}
                    label="Payment Method"
                  >
                    <MenuItem value="Espèces">Cash</MenuItem>
                    <MenuItem value="Chèque">Check</MenuItem>
                    <MenuItem value="Virement">Bank Transfer</MenuItem>
                    <MenuItem value="Carte">Card</MenuItem>
                  </Select>
                </FormControl>

                <TextField
                  fullWidth
                  label="Payment Reference"
                  name="reference_paiement"
                  value={formData.reference_paiement}
                  onChange={handleChange}
                  margin="normal"
                />

                <TextField
                  fullWidth
                  label="Comments"
                  name="commentaire"
                  value={formData.commentaire}
                  onChange={handleChange}
                  margin="normal"
                  multiline
                  rows={3}
                />

                <Button
                  variant="contained"
                  color="secondary"
                  onClick={calculateInstallments}
                  sx={{ mt: 2 }}
                  fullWidth
                  disabled={
                    !formData.numberOfInstallments ||
                    !formData.firstPaymentAmount ||
                    !formData.firstPaymentDate
                  }
                >
                  Calculate Installments
                </Button>
              </CardContent>
            </Card>
          </Grid>

          {/* Right Side - Installments Table */}
          <Grid item xs={12} md={6}>
            <Card variant="outlined" sx={{ height: "100%" }}>
              <CardContent>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 2,
                  }}
                >
                  <Typography variant="h6">
                    Installments Overview
                    {installments.length > 0 && (
                      <>
                        <Typography variant="subtitle1" color="text.secondary">
                          Total: €{calculateTotalAmount().toFixed(2)}
                        </Typography>
                        <Typography variant="subtitle1" color="text.secondary">
                          Remaining: €{calculateRemainingAmount().toFixed(2)}
                        </Typography>
                      </>
                    )}
                  </Typography>
                  {installments.length > 0 &&
                    calculateRemainingAmount() > 0 && (
                      <Tooltip title="Add new installment">
                        <IconButton
                          onClick={handleAddNewInstallment}
                          disabled={isAddingNew || editingInstallment !== null}
                          color="primary"
                        >
                          <AddIcon />
                        </IconButton>
                      </Tooltip>
                    )}
                </Box>

                {installments.length > 0 ? (
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>#</TableCell>
                          <TableCell>Due Date</TableCell>
                          <TableCell>Amount (€)</TableCell>
                          <TableCell>Status</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {installments.map((installment) => (
                          <TableRow
                            key={installment.numero_tranche}
                            onDoubleClick={() =>
                              handleInstallmentDoubleClick(installment)
                            }
                            sx={{
                              cursor: "pointer",
                              backgroundColor:
                                installment.numero_tranche === 1
                                  ? "rgba(0, 0, 0, 0.04)"
                                  : "inherit",
                              "&:hover": {
                                backgroundColor: "rgba(0, 0, 0, 0.08)",
                              },
                            }}
                          >
                            <TableCell>{installment.numero_tranche}</TableCell>
                            <TableCell>
                              {editingInstallment?.numero_tranche ===
                              installment.numero_tranche ? (
                                <TextField
                                  type="date"
                                  name="date_echeance_edit"
                                  value={editingInstallment.date_echeance_edit}
                                  onChange={handleEditChange}
                                  size="small"
                                  fullWidth
                                />
                              ) : (
                                installment.date_echeance
                              )}
                            </TableCell>
                            <TableCell>
                              {editingInstallment?.numero_tranche ===
                              installment.numero_tranche ? (
                                <TextField
                                  type="number"
                                  name="montant_edit"
                                  value={editingInstallment.montant_edit}
                                  onChange={handleEditChange}
                                  size="small"
                                  inputProps={{ step: "0.01" }}
                                  fullWidth
                                />
                              ) : (
                                installment.montant.toFixed(2)
                              )}
                            </TableCell>
                            <TableCell>
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 1,
                                }}
                              >
                                {installment.numero_tranche === 1 ? (
                                  <Typography color="success.main">
                                    Payé
                                  </Typography>
                                ) : (
                                  <Typography color="warning.main">
                                    En attente
                                  </Typography>
                                )}
                                {editingInstallment?.numero_tranche ===
                                  installment.numero_tranche && (
                                  <Box sx={{ ml: 1 }}>
                                    <Button
                                      size="small"
                                      variant="contained"
                                      color="secondary"
                                      onClick={handleSaveEdit}
                                      sx={{ mr: 1 }}
                                    >
                                      Save
                                    </Button>
                                    <Button
                                      size="small"
                                      onClick={() =>
                                        setEditingInstallment(null)
                                      }
                                    >
                                      Cancel
                                    </Button>
                                  </Box>
                                )}
                              </Box>
                            </TableCell>
                          </TableRow>
                        ))}
                        {isAddingNew && (
                          <TableRow>
                            <TableCell>{installments.length + 1}</TableCell>
                            <TableCell>
                              <TextField
                                type="date"
                                value={newInstallment.date_echeance}
                                onChange={(e) =>
                                  setNewInstallment((prev) => ({
                                    ...prev,
                                    date_echeance: e.target.value,
                                  }))
                                }
                                size="small"
                                fullWidth
                              />
                            </TableCell>
                            <TableCell>
                              <TextField
                                type="number"
                                value={newInstallment.montant}
                                onChange={(e) =>
                                  setNewInstallment((prev) => ({
                                    ...prev,
                                    montant: e.target.value,
                                  }))
                                }
                                size="small"
                                inputProps={{ step: "0.01" }}
                                fullWidth
                              />
                            </TableCell>
                            <TableCell>
                              <Box sx={{ display: "flex", gap: 1 }}>
                                <Button
                                  size="small"
                                  variant="contained"
                                  color="secondary"
                                  onClick={handleSaveNewInstallment}
                                >
                                  Save
                                </Button>
                                <Button
                                  size="small"
                                  onClick={() => {
                                    setIsAddingNew(false);
                                    setNewInstallment({
                                      date_echeance: "",
                                      montant: "",
                                    });
                                  }}
                                >
                                  Cancel
                                </Button>
                              </Box>
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      height: "200px",
                      backgroundColor: "action.hover",
                      borderRadius: 1,
                    }}
                  >
                    <Typography color="text.secondary">
                      Fill in the payment details and click "Calculate
                      Installments"
                    </Typography>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="secondary"
          disabled={
            loading ||
            installments.length === 0 ||
            editingInstallment !== null ||
            isAddingNew
          }
        >
          {loading ? (
            <CircularProgress size={24} color="inherit" />
          ) : (
            "Create Installment Plan"
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AchatInstallmentPaymentModal;
